<template>
    <x-dialog v-model="editDialog" persistent width="800">
        <template v-slot:activator="{ on }">
            <x-btn
                color="tertiary"
                :class="action === 'edit' ? 'ma-0' : 'mt-2'"
                small
                :outline="action === 'edit'"
                v-on="on"
            >
                {{ action | capitalize }} security
            </x-btn>
        </template>

        <x-form ref="form" @submit.prevent="submit">
            <x-container fluid grid-list-lg>
                <x-layout>
                    <x-flex xs12>
                        <h2>{{ action | capitalize }} Security</h2>
                    </x-flex>
                </x-layout>

                <x-layout>
                    <x-flex xs12>
                        <x-text-field
                            v-model="activeSecurity.name"
                            class="required"
                            label="Name"
                            required
                            name="name"
                            maxlength="1000"
                            :rules="[v => ruleRequired(v, 'Name')]"
                        />

                        <x-select
                            v-model="activeSecurity.status"
                            :items="['active', 'inactive']"
                            label="Status"
                        />
                        <x-textarea
                            v-model="activeSecurity.eea_signature_text"
                            label="EEA Signature"
                            placeholder=" "
                            outline
                            maxlength="1000"
                        />
                    </x-flex>
                </x-layout>

                <x-layout v-for="(identifier, index) in identifiers" :key="index">
                    <x-flex xs6>
                        <x-autocomplete
                            v-model="identifier.type"
                            :items="identifierTypes"
                            class="required"
                            label="Identifier type"
                            placeholder="Identifier type"
                            item-value="type"
                            item-text="name"
                            :rules="[
                                    v => ruleRequired(v, 'Identifier type'),
                                    v => identifiers.filter(({ type }) => type === v).length > 1 ? 'Identifier type already in use' : true
                                ]"
                            append-icon="keyboard_arrow_down"
                            outline
                        />
                    </x-flex>

                    <x-flex>
                        <x-text-field
                            v-model="identifier.value"
                            class="required"
                            label="Identifier"
                            placeholder=" "
                            :rules="[v => ruleRequired(v, 'Identifier')]"
                        />
                    </x-flex>

                    <x-flex class="grow-0">
                        <x-btn
                            class="square-icon label-spacing"
                            color="tertiary"
                            small
                            @click="removeIdentifier(index)"
                        >
                            <i class="material-icons-outlined">delete_forever</i>
                        </x-btn>
                    </x-flex>
                </x-layout>

                <x-layout class="response-actions" justify-space-between>
                    <x-flex xs3 class="text-xs-left">
                        <x-btn
                            color="tertiary"
                            small
                            @click="editDialog = false"
                        >
                            Cancel
                        </x-btn>
                    </x-flex>

                    <x-flex xs9 class="text-xs-right">
                        <x-btn
                            class="mr-4"
                            color="tertiary"
                            small
                            @click="addIdentifier"
                            :disabled="identifiers.length >= identifierTypes.length"
                        >
                            Add identifier
                        </x-btn>

                        <x-btn
                            class="ma-0"
                            color="primary"
                            small
                            type="submit"
                            :loading="loading"
                        >
                            Save

                            <i class="material-icons ml-2">trending_flat</i>
                        </x-btn>
                    </x-flex>
                </x-layout>
            </x-container>
        </x-form>
        <modal
            ref="confirmEdit"
            title="Confirm edit"
            confirmation
        >
            <div class="text-xs-left">
                <p>
                    Editing this security will apply the changes retroactively to all risks using this security.
                </p>
                <p>
                    Are you sure you want to make this change?
                </p>
            </div>
        </modal>
    </x-dialog>
</template>

<script>
import rules from '~/mixins/rules';
import Modal from '~/components/Shared/UIComponents/Modal';

export default {
        name: "NewAndEditSecurity",
        mixins: [rules],
        components: {
            Modal
        },
        props: {
            security: {
                type: Object,
                default: () => {}
            },
            action: {
                type: String,
                default: 'new'
            }
        },
        data() {
            return {
                editDialog: false,
                activeSecurity: {},
                loading: false,
                identifiers: [],
                identifierTypes: [
                    {
                        type: 'lei',
                        field: 'legal_entity_identifier',
                        name: 'Legal Entity Identifier (LEI)',
                    },
                    {
                        type: 'duns',
                        field: 'dun_and_bradstreet_identifier',
                        name: 'Dun & Bradstreet (D-U-N-S)',
                    },
                    {
                        type: 'ein',
                        field: 'employer_identification_number_identifier',
                        name: 'Employer Identification Number (EIN)',
                    },
                    {
                        type: 'syn',
                        field: 'syndicate_number_identifier',
                        name: 'Syndicate Number',
                    },
                    {
                        type: 'bnpp',
                        field: 'bnpp_technical_identifier',
                        name: 'BNPP Technical ID',
                    },
                ],
            }
        },
        watch: {
            editDialog(newValue) {
                if (newValue) {
                    this.activeSecurity = { ...this.security };
                    this.identifiers = this.identifierTypes
                        .map(({ type, field }) => ({
                            type,
                            value: this.activeSecurity[field],
                        }))
                        .filter(({ value }) => value);
                    this.$refs.form.resetValidation();
                }
            }
        },
        methods: {
            addIdentifier() {
                this.identifiers.push({});
            },
            removeIdentifier(index) {
                this.identifiers.splice(index, 1);
            },
            async submit() {
                if (this.$refs.form.validate()) {
                    this.loading = true;
                    const identifiers = this.identifierTypes.reduce((acc, { type, field }) => {
                        acc[field] = this.identifiers.find(({type: t}) => t === type)?.value;

                        return acc;
                    }, {});
                    const payload = {
                        ...this.activeSecurity,
                        ...identifiers,
                    };
                    if (this.activeSecurity.id === undefined) {
                        this.newSecurity(payload)
                    } else {
                        if (await this.$refs.confirmEdit.open() === false) {
                            this.loading = false;
                            return;
                        }
                        this.editSecurity(payload)
                    }
                }
            },
            newSecurity(payload) {
                axios.post(
                    this.$root.api('insurer/security'), payload)
                    .then(() => {
                        this.$router.go();
                    });
            },
            editSecurity(payload) {
                axios.put(
                    this.$root.api('insurer/security/') + this.activeSecurity.id, payload)
                    .then(() => {
                        this.$router.go();
                    });
            }
        }
    }
</script>
